import moment from 'moment'
import { Provider, WorkingSchedule } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import { formatMilitaryTimeTo12Hour } from '@utils/time.util'
import { WorkStatus } from '@constants/Providers'

export type ShiftKind = 'shift' | 'day-off' | 'half-day' | 'pto' | 'closed'

export type DayShift = {
  kind: ShiftKind
  label: string
  /** Date-specific override time shown below the regular schedule. */
  overrideLabel?: string
}

const ISO_DATE_OVERRIDE = /^\d{4}-\d{2}-\d{2}$/

export const isDateOverrideRow = (dayOfWeek: string): boolean =>
  ISO_DATE_OVERRIDE.test(dayOfWeek)

export const weekdayHours = (
  rows: WorkingSchedule[] | undefined,
): WorkingSchedule[] =>
  rows?.filter((r) => !isDateOverrideRow(r.day_of_week)) ?? []

const dateOverrideForDay = (
  rows: WorkingSchedule[] | undefined,
  day: moment.Moment,
): WorkingSchedule | undefined => {
  const dateKey = day.format('YYYY-MM-DD')
  return rows?.find(
    (r) => isDateOverrideRow(r.day_of_week) && r.day_of_week === dateKey,
  )
}

export type StaffWeek = {
  staffId: string
  staffName: string
  workRole?: string
  workStatus?: string
  shifts: DayShift[]
}

const stripMinutes = (label: string): string =>
  label.replace(/:00\s/, ' ').trim()

export const formatShift = (start?: string, end?: string): string => {
  if (!start || !end) return ''
  const s = stripMinutes(formatMilitaryTimeTo12Hour(start))
  const e = stripMinutes(formatMilitaryTimeTo12Hour(end))
  return s && e ? `${s} - ${e}` : ''
}

export const findDayRow = (
  rows: WorkingSchedule[] | undefined,
  dayName: string,
): WorkingSchedule | undefined => rows?.find((r) => r.day_of_week === dayName)

export const cellForDay = (staff: Provider, day: moment.Moment): DayShift => {
  const dayName = day.format('dddd')
  const hours = weekdayHours(staff.workingHours)
  const row = findDayRow(hours, dayName)

  let kind: ShiftKind = 'day-off'
  let label = 'Day-off'

  if (row) {
    const useSame = staff.same_working_hours === true
    const start = useSame ? staff.shift_start_time : row.start_time
    const end = useSame ? staff.shift_end_time : row.end_time
    const shiftLabel = formatShift(start, end)
    if (shiftLabel) {
      kind = 'shift'
      label = shiftLabel
    }
  }

  const override = dateOverrideForDay(staff.workingHours, day)
  const overrideLabel = override
    ? formatShift(override.start_time, override.end_time) || undefined
    : undefined

  return overrideLabel ? { kind, label, overrideLabel } : { kind, label }
}

const CLOSED_SHIFT: DayShift = { kind: 'closed', label: 'Closed' }
const PTO_SHIFT: DayShift = { kind: 'pto', label: 'PTO' }

/** Clinic-closed weekdays win over everything; on-leave staff show PTO. */
export const buildWeek = (
  staff: Provider,
  days: moment.Moment[],
  closedWeekdays: ReadonlySet<string> = new Set(),
): DayShift[] =>
  days.map((d) => {
    if (closedWeekdays.has(d.format('dddd'))) return CLOSED_SHIFT
    if (staff.work_status === WorkStatus.ON_LEAVE) return PTO_SHIFT
    return cellForDay(staff, d)
  })

export const toStaffWeek = (
  staff: Provider,
  days: moment.Moment[],
  closedWeekdays?: ReadonlySet<string>,
): StaffWeek => ({
  staffId: staff.provider_id,
  staffName: getFullName(staff) || staff.username || '—',
  workRole: staff.work_role,
  workStatus: staff.work_status,
  shifts: buildWeek(staff, days, closedWeekdays),
})
